import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { ParameterDefinitionDto } from '@/api/types'
import {
  groupsQuery,
  parameterDefinitionsQuery,
  useClearGroupParameter,
  useSetGroupParameter,
} from '@/features/platform/platform.queries'
import { formatParameterValue, unitFor } from '@/features/platform/parameterValue'
import { cn } from '@/lib/cn'
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  TextField,
  useToast,
} from '@/ui'
import { IconEdit } from '@/icons'

export const Route = createFileRoute('/_authed/parameters/')({
  component: ParametersPage,
})

interface EditTarget {
  readonly parameter: ParameterDefinitionDto
  readonly groupId: string
  readonly groupName: string
  /** The override in BASE units, or null when the group inherits the default. */
  readonly current: number | null
}

function ParametersPage() {
  const { t, i18n } = useTranslation()
  const toast = useToast()

  const definitions = useQuery(parameterDefinitionsQuery)
  const groups = useQuery(groupsQuery)

  const setValue = useSetGroupParameter()
  const clearValue = useClearGroupParameter()

  const [editing, setEditing] = useState<EditTarget | null>(null)
  const [draft, setDraft] = useState('')

  const platformGroups = (groups.data?.items ?? []).filter((group) => group.worldId === null)

  if (definitions.isPending) return <LoadingState />
  if (definitions.isError) {
    return (
      <ErrorState
        error={definitions.error}
        onRetry={() => {
          void definitions.refetch()
        }}
      />
    )
  }

  const items = definitions.data.items

  function open(target: EditTarget) {
    const unit = unitFor(target.parameter.type)
    setEditing(target)
    // Shown in the readable unit — nobody should type 2147483648 to mean two gigabytes.
    setDraft(target.current === null ? '' : String(unit.fromBase(target.current)))
  }

  function save() {
    if (!editing) return

    const trimmed = draft.trim()
    const onError = (error: unknown) => {
      toast.fromError(error, t('state.errorTitle'))
    }

    /*
     * An empty box means "use the default", and that is a DELETE.
     *
     * Sending it as a PUT of null would be coerced to 0 by the API's validator — turning
     * "no limit set" into "allow nothing", the exact opposite of the intent.
     */
    if (trimmed === '') {
      clearValue.mutate(
        { groupId: editing.groupId, key: editing.parameter.key },
        {
          onSuccess: () => {
            toast.success(t('parameters.cleared'))
            setEditing(null)
          },
          onError,
        },
      )

      return
    }

    const entered = Number(trimmed)
    if (!Number.isFinite(entered) || entered < 0) return

    setValue.mutate(
      {
        groupId: editing.groupId,
        key: editing.parameter.key,
        value: unitFor(editing.parameter.type).toBase(entered),
      },
      {
        onSuccess: () => {
          toast.success(t('parameters.updated'))
          setEditing(null)
        },
        onError,
      },
    )
  }

  const editingUnit = editing ? unitFor(editing.parameter.type) : null

  return (
    <>
      <PageHeader title={t('parameters.title')} description={t('parameters.subtitle')} />

      {items.length === 0 ? (
        <Card>
          <EmptyState title={t('parameters.empty')} description={t('parameters.emptyHint')} />
        </Card>
      ) : (
        <Card isFlush>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <caption className="sr-only">{t('parameters.title')}</caption>
              <thead>
                <tr className="border-b border-border">
                  <th
                    scope="col"
                    className="sticky left-0 z-10 bg-surface-raised px-4 py-2.5 text-xs font-medium tracking-wide text-text-secondary uppercase"
                  >
                    {t('parameters.columnLimit')}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-2.5 text-right text-xs font-medium tracking-wide text-text-secondary uppercase"
                  >
                    {t('parameters.columnDefault')}
                  </th>
                  {platformGroups.map((group) => (
                    <th
                      key={group.id}
                      scope="col"
                      className="px-4 py-2.5 text-right text-xs font-medium tracking-wide whitespace-nowrap text-text-secondary uppercase"
                    >
                      {group.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((parameter) => (
                  <tr key={parameter.key} className="group/row hover:bg-surface-sunken/40">
                    {/* Sticky so the limit stays readable while the group columns scroll. */}
                    <th
                      scope="row"
                      className="sticky left-0 z-10 min-w-64 bg-surface-raised px-4 py-3 text-left font-normal group-hover/row:bg-surface-sunken"
                    >
                      <p className="text-[0.8125rem] font-medium text-text">{parameter.name}</p>
                      {/* One line, full text on hover: a wrapped description made every row
                          six lines tall and the table impossible to compare across. */}
                      <p
                        className="truncate text-xs text-text-secondary"
                        title={parameter.description}
                      >
                        {parameter.description}
                      </p>
                      <code className="mt-0.5 block font-mono text-[0.6875rem] text-text-muted">
                        {parameter.key}
                      </code>
                    </th>

                    <td
                      data-numeric
                      className="px-4 py-3 text-right text-[0.8125rem] whitespace-nowrap text-text-secondary"
                    >
                      {formatParameterValue(parameter.defaultValue, parameter.type, i18n.language)}
                    </td>

                    {platformGroups.map((group) => {
                      const override = parameter.groupOverrides.find(
                        (entry) => entry.roleId === group.id,
                      )

                      return (
                        <td key={group.id} className="px-2 py-3 text-right">
                          <button
                            type="button"
                            aria-label={t('parameters.editFor', {
                              limit: parameter.name,
                              group: group.name,
                            })}
                            onClick={() => {
                              open({
                                parameter,
                                groupId: group.id,
                                groupName: group.name,
                                current: override ? Number(override.value) : null,
                              })
                            }}
                            className={cn(
                              'inline-flex w-full items-center justify-end gap-1.5 rounded-control',
                              'border px-2 py-1 text-[0.8125rem] whitespace-nowrap transition-colors',
                              // Always looks like a control. The previous version rendered a bare
                              // word with the pencil hidden until hover, so the whole table read
                              // as static text and nobody could tell it was editable.
                              override
                                ? 'border-primary/30 bg-primary-subtle font-medium text-primary'
                                : 'border-border text-text-muted hover:border-border-strong hover:text-text',
                            )}
                          >
                            <span data-numeric>
                              {override
                                ? formatParameterValue(
                                    override.value,
                                    parameter.type,
                                    i18n.language,
                                  )
                                : t('parameters.notSet')}
                            </span>
                            <IconEdit size={11} className="shrink-0 opacity-50" />
                          </button>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="border-t border-border px-4 py-2.5 text-xs text-text-secondary">
            {t('parameters.tableHint')}
          </p>
        </Card>
      )}

      <Dialog
        isOpen={editing !== null}
        onClose={() => {
          setEditing(null)
        }}
        title={t('parameters.editFor', {
          limit: editing?.parameter.name ?? '',
          group: editing?.groupName ?? '',
        })}
        description={editing?.parameter.description}
        size="sm"
        isDismissable={!setValue.isPending && !clearValue.isPending}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setEditing(null)
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={save}
              isLoading={setValue.isPending || clearValue.isPending}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <TextField
          label={
            editingUnit?.label
              ? t('parameters.valueInUnit', { unit: editingUnit.label })
              : t('parameters.valueLabel')
          }
          hint={t('parameters.valueHint')}
          type="number"
          inputMode="decimal"
          min={0}
          step={editingUnit?.step ?? 1}
          value={draft}
          autoFocus
          onChange={(event) => {
            setDraft(event.target.value)
          }}
        />

        <p className="mt-3 flex items-center gap-2 text-xs text-text-secondary">
          <Badge>{t('parameters.columnDefault')}</Badge>
          {editing
            ? formatParameterValue(
                editing.parameter.defaultValue,
                editing.parameter.type,
                i18n.language,
              )
            : null}
        </p>
      </Dialog>
    </>
  )
}
